<template>
  <vl-grid>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsInputField
        name="label"
        :label="t('productgroup.detail.label')"
        :placeholder="t('productgroup.detail.label')"
      />
    </vl-form-column>
    <vl-form-column v-vl-spacer:bottom.small>
      <PbsTextAreaField
        name="description"
        :label="t('productgroup.detail.description')"
        :placeholder="t('productgroup.detail.description')"
      />
    </vl-form-column>
  </vl-grid>

  <FieldArray v-slot="{ fields, remove, push }" name="emails">
    <vl-grid v-vl-spacer:top.medium>
      <vl-column width="8" width-xs="12">
        <vl-title tag-name="h4"> {{ $t('productgroup.detail.emails') }} </vl-title>
      </vl-column>
      <vl-column width="4" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button type="button" icon="add" mod-icon @click="push('')"></vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid v-for="(field, idx) in fields" :key="field.key">
      <vl-column width="6" width-xs="8">
        <PbsInputField :name="`emails[${idx}]`" :placeholder="t('productgroup.create.emailPlaceholder')" />
      </vl-column>
      <vl-column width="1" width-xs="4">
        <vl-action-group>
          <vl-button type="button" icon="trash" mod-icon mod-error @click="remove(idx)"></vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid v-if="!fields.length">
      <vl-column width="12" width-xs="12">
        {{ $t('productgroup.detail.noEmails') }}
      </vl-column>
    </vl-grid>
    <PbsErrorField name="emails" />
  </FieldArray>

  <FieldArray v-slot="{ fields, remove, push }" name="productProcessDefinitions">
    <vl-grid v-vl-spacer:top.medium>
      <vl-column width="8" width-xs="12">
        <vl-title tag-name="h4"> {{ $t('productgroup.detail.members') }} </vl-title>
      </vl-column>
      <vl-column width="4" width-xs="12">
        <vl-action-group mod-align-right>
          <vl-button
            type="button"
            icon="add"
            mod-icon
            @click="push({ productProcessDefinition: null, switchDependency: false })"
          ></vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid v-for="(field, idx) in fields" :key="field.key" v-vl-spacer:bottom.small>
      <vl-column width="7" width-xs="12">
        <PbsSelectField
          :name="`productProcessDefinitions[${idx}].productProcessDefinition`"
          :label="t('productgroup.detail.member.label')"
          :placeholder="t('productgroup.detail.member.label')"
          :options="selectableDefinitions"
          :custom-label="getDefinitionLabel"
          :sortFunction="definitionSortFunction"
        />
      </vl-column>
      <vl-column width="4" width-xs="8">
        <PbsRadioButtonField
          :name="`productProcessDefinitions[${idx}].switchDependency`"
          :label="t('productgroup.detail.member.switchDependency')"
        />
      </vl-column>
      <vl-column width="1" width-xs="4">
        <vl-action-group>
          <vl-button type="button" icon="trash" mod-icon mod-error @click="remove(idx)"></vl-button>
        </vl-action-group>
      </vl-column>
    </vl-grid>
    <vl-grid v-if="!fields.length">
      <vl-column width="12" width-xs="12">
        {{ $t('productgroup.detail.noMembers') }}
      </vl-column>
    </vl-grid>
    <PbsErrorField name="productProcessDefinitions" />
  </FieldArray>
</template>

<script lang="ts" setup>
import { computed, onMounted, ref, Ref } from 'vue';
import { FieldArray, useFormValues } from 'vee-validate';
import { useI18n } from 'vue-i18n';
import { ProductGroupSelectableDefinitionDto } from '@/api/backoffice/clients';
import { useProductGroupDefinitionsStore } from '@/modules/configuration/store/product-group/product-group-definitions.store';
import PbsInputField from '@/components/forms/PbsInputField.vue';
import PbsTextAreaField from '@/components/forms/PbsTextAreaField.vue';
import PbsSelectField from '@/components/forms/PbsSelectField.vue';
import PbsRadioButtonField from '@/components/forms/PbsRadioButtonField.vue';
import PbsErrorField from '@/components/forms/PbsErrorField.vue';

// The fields bind to the form of the parent, which owns useForm and the submit handler.
const { t } = useI18n();
const definitionStore = useProductGroupDefinitionsStore();

const definitionOptions: Ref<ProductGroupSelectableDefinitionDto[]> = ref([]);
const formValues = useFormValues<any>();

/*
  The endpoint only returns definitions in Draft or Actief. An existing group may still hold a member
  that has moved on to another status though, and dropping it from the options would leave its row
  blank and silently lose the membership on save. So whatever is already picked stays selectable, with
  the data connection the detail carries for it.
*/
const selectableDefinitions = computed((): ProductGroupSelectableDefinitionDto[] => {
  const selectableIds = new Set(definitionOptions.value.map((definition) => definition.id));

  const alreadySelected = (formValues.value?.productProcessDefinitions ?? [])
    .filter((member: any) => member?.productProcessDefinition?.id && !selectableIds.has(member.productProcessDefinition.id))
    .map((member: any) => ({
      id: member.productProcessDefinition.id,
      label: member.productProcessDefinition.label,
      dataConnectionCode: member.dataConnectionCode
    }));

  return [...definitionOptions.value, ...alreadySelected];
});

onMounted(async () => {
  definitionOptions.value = await definitionStore.getSelectableDefinitions();
});

/*
  PbsSelect sorts the options itself, on the rendered label. That label starts with the id, so without
  a sort function of its own the list comes out ordered by id instead of by name.
*/
const definitionSortFunction = (a: ProductGroupSelectableDefinitionDto, b: ProductGroupSelectableDefinitionDto) => {
  return (a?.label ?? '').localeCompare(b?.label ?? '');
};

const getDefinitionLabel = (entry: ProductGroupSelectableDefinitionDto) => {
  const label = `[${entry?.id}] ${entry?.label}`;

  return entry?.dataConnectionCode ? `${label} (${entry.dataConnectionCode})` : label;
};
</script>
